// Sentry plumbing.
//
// Gated off by default. Two env vars must both be set for telemetry to
// initialise:
//
//   VITE_SENTRY_DSN          — your Sentry project DSN (secret)
//   VITE_ENABLE_TELEMETRY    — "on" | "true" | "1" (anything else = off)
//
// On localhost with no `.env.local`, both are empty → `initTelemetry()`
// returns early → zero network traffic → Sentry SDK's global hooks are
// never installed.
//
// In staging / production, set both in your deploy platform's secret
// manager (Vercel env, Netlify env, etc.) — never commit a DSN to the repo.
//
// ADR: docs/adr/0006-sentry-telemetry.md
// Related: prof-backlog.md #1, kondo-parity.md §5.5.1.

import * as Sentry from '@sentry/react'
import { env } from './env'
import { installSink, type LogEntry } from './logger'
import { ErrorCode, isAborted, type AppError } from './errors'

// ─── State ───────────────────────────────────────────────────────────────────

let _initialized = false
let _uninstallLoggerSink: (() => void) | null = null

// ─── Control-flow filter ─────────────────────────────────────────────────────

/**
 * Errors that are expected control flow, not problems worth paging on.
 * Dropped from Sentry at both the `beforeSend` hook and the logger sink.
 *
 * Extend carefully — once a code is added here, every instance of it
 * disappears from Sentry. That's the point (noise reduction) but it
 * also means a subsequent "why is this broken for users?" investigation
 * has no breadcrumbs. Prefer dropping only codes that fire predictably
 * in a normal-user no-error path.
 */
const CONTROL_FLOW_CODES: ReadonlySet<string> = new Set([
  // Fires on every query that hits the extension from a browser without
  // the extension installed. At beta scale with the extension as opt-in,
  // this would drown real signal within a day.
  ErrorCode.EXTENSION_MISSING,
])

function isControlFlow(e: unknown): boolean {
  if (isAborted(e)) return true
  const code = (e as Partial<AppError> | null | undefined)?.code
  return typeof code === 'string' && CONTROL_FLOW_CODES.has(code)
}

// ─── Gate ────────────────────────────────────────────────────────────────────

/**
 * True when both `VITE_SENTRY_DSN` and `VITE_ENABLE_TELEMETRY` are set.
 *
 * Exported so callers can decide whether to spend cycles shaping a
 * context object before calling `reportError()`.
 */
export function isTelemetryEnabled(): boolean {
  if (!env.sentryDsn) return false
  const flag = (import.meta.env as Record<string, string | undefined>).VITE_ENABLE_TELEMETRY
  return flag === 'on' || flag === 'true' || flag === '1'
}

// ─── Init ────────────────────────────────────────────────────────────────────

/**
 * Initialise Sentry if both gate env vars are set. Idempotent — safe to
 * call multiple times. No-op when the gate is off.
 *
 * Call this from `main.tsx` before the React root mounts so global error
 * handlers are live during initial render. Logger sink is installed so
 * any error-level `createLogger(...).error(...)` call gets captured.
 */
export function initTelemetry(): void {
  if (_initialized) return
  if (!isTelemetryEnabled()) return

  Sentry.init({
    dsn: env.sentryDsn!, // non-null per `isTelemetryEnabled` check
    environment: env.appEnv,
    // We control which data reaches Sentry via `beforeSend`; don't let
    // the SDK auto-include user IP / cookies / headers.
    sendDefaultPii: false,
    // Perf / replay deferred — we only need error capture for now.
    tracesSampleRate: 0,
    // Bail on silent-cancel errors (AbortController, route changes etc.)
    // and other control-flow shapes before they reach Sentry — these are
    // normal flow, not problems we want to page on.
    beforeSend(event, hint) {
      if (hint?.originalException && isControlFlow(hint.originalException)) {
        return null
      }
      return event
    },
  })

  _uninstallLoggerSink = installSink(forwardLogToSentry)
  _initialized = true
}

/**
 * Tear down for tests + hot-reload. Not called in production flows.
 * Sentry SDK doesn't expose a public `reset()` — we just drop our
 * own flags so subsequent `initTelemetry()` calls can re-run.
 */
export function _resetForTests(): void {
  _initialized = false
  if (_uninstallLoggerSink) {
    _uninstallLoggerSink()
    _uninstallLoggerSink = null
  }
}

// ─── User identity ───────────────────────────────────────────────────────────

/**
 * Tag subsequent events with the signed-in user's id. Call from
 * `AuthProvider` when a session appears. Pass `null` on sign-out.
 *
 * Deliberately does NOT send email / display_name / avatar by default —
 * those are personally identifying. When we ship user-facing bug-report
 * forms we'll revisit and ask for consent.
 */
export function setTelemetryUser(user: { id: string } | null): void {
  if (!_initialized) return
  if (user == null) {
    Sentry.setUser(null)
    return
  }
  Sentry.setUser({ id: user.id })
}

// ─── Explicit reporting ──────────────────────────────────────────────────────

export interface ReportErrorContext {
  /** Short scope name — e.g. 'crm-sync', 'prefs-migrate'. Tagged on the event. */
  scope?: string
  /** Free-form extra data. Shown in Sentry's "Extra" panel. Stripped of unknown-shape values. */
  extra?: Record<string, unknown>
}

/**
 * Send an error to Sentry, if enabled. No-op on localhost.
 *
 * Prefer throwing / returning `AppError` shapes (ADR 0003) and letting
 * the logger sink catch them — this helper is for explicit "I want this
 * in Sentry whether or not it was logged" reporting.
 */
export function reportError(err: unknown, context?: ReportErrorContext): void {
  if (!_initialized) return
  if (isAborted(err)) return

  const opts: { tags?: Record<string, string>; extra?: Record<string, unknown> } = {}
  if (context?.scope) opts.tags = { scope: context.scope }
  if (context?.extra) opts.extra = context.extra
  if (Object.keys(opts).length === 0) {
    Sentry.captureException(err)
  } else {
    Sentry.captureException(err, opts)
  }
}

/**
 * Send a message (non-error) to Sentry. Use sparingly — errors are
 * more useful signal. Handy for "this rare branch fired, want to know
 * about it" observations.
 */
export function reportMessage(
  message: string,
  level: 'info' | 'warning' | 'error' = 'info',
  context?: ReportErrorContext,
): void {
  if (!_initialized) return
  Sentry.captureMessage(message, {
    level,
    tags: context?.scope ? { scope: context.scope } : undefined,
    extra: context?.extra,
  })
}

// ─── Logger sink ─────────────────────────────────────────────────────────────

/**
 * Forward error-level log entries to Sentry. Installed by `initTelemetry()`.
 *
 * Only `error` level goes through — `warn` is often noise (e.g. network
 * blips retried automatically) and would burn Sentry quota fast. Code
 * that genuinely wants a warn-level event in Sentry should call
 * `reportMessage(msg, 'warning')` explicitly.
 */
function forwardLogToSentry(entry: LogEntry): void {
  if (entry.level !== 'error') return
  if (isControlFlow(entry.error ?? entry.args[0])) return

  const tagScope = entry.scope
  if (entry.error) {
    // Thread any AppError `code` through as a tag for filtering.
    const code = (entry.error as Partial<AppError>).code
    Sentry.captureException(entry.error, {
      tags: code ? { scope: tagScope, 'error.code': code } : { scope: tagScope },
      extra: { args: safelySerialize(entry.args) },
    })
  } else {
    const messageParts = entry.args
      .map((a) => (typeof a === 'string' ? a : safeStringify(a)))
      .join(' ')
    Sentry.captureMessage(`[${tagScope}] ${messageParts}`, {
      level: 'error',
      tags: { scope: tagScope },
    })
  }
}

function safeStringify(v: unknown): string {
  try {
    return JSON.stringify(v)
  } catch {
    return String(v)
  }
}

function safelySerialize(args: unknown[]): unknown {
  try {
    return JSON.parse(JSON.stringify(args, (_k, v) => {
      // Replace Error instances with their message + name to avoid
      // stringifying the whole stack in the extras; Sentry already
      // captures the exception proper via `captureException`.
      if (v instanceof Error) return { name: v.name, message: v.message }
      return v
    }))
  } catch {
    return '[unserializable]'
  }
}
